import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import HeaderTwo from "../components/header2";
import Footer from "../components/footer";
import s from "../styles/community.module.css";
import {
    COMMUNITY_TABS, CommunityPost, LikePend, PostBody, PostStat, RawPost,
    byKey, byNewest, fetchPostBody, fetchPosts, fetchStats, fmtN, heartsShown,
    loadLikePend, loadLiked, mergePosts, newFrom, pickBest, saveLikes, sendHit, sendLike,
} from "../lib/sunbaeBlog";

/**
 * Community — 커뮤니티 (/community). claude.ai/design `헤더+칼럼.dc.html` 포팅.
 * 목록: 주간 인기 글 레일 + 탭·검색·정렬 + 썸네일 그리드 + 페이지네이션.
 * 본문: ?post=<글번호> (같은 컴포넌트, 브라우저 뒤로가기로 목록 복귀). ?tab=0~4 진입 시 해당 탭 목록으로 이동.
 */

const SYMBOL = "/img/community/sunbae-symbol.svg";
const LIST_LABEL = "community-list";

const ChevL = ({ w = 18, sw = 1.4 }: { w?: number; sw?: number }) => (
    <svg width={w} height={w} viewBox="0 0 20 20" fill="none"><path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth={sw} /></svg>
);
const ChevR = ({ w = 18, sw = 1.4 }: { w?: number; sw?: number }) => (
    <svg width={w} height={w} viewBox="0 0 20 20" fill="none"><path d="m7.5 4.5 5.5 5.5-5.5 5.5" stroke="currentColor" strokeWidth={sw} /></svg>
);

const Badges: React.FC<{ cat: string; isNew?: boolean; className?: string }> = ({ cat, isNew, className }) => (
    <span className={`${s.badges} ${className || ""}`}>
        {isNew && <span className={`${s.badge} ${s.bNew}`}>N</span>}
        {cat === "멘토 칼럼" && <span className={`${s.badge} ${s.bMentor}`}>멘토 칼럼</span>}
        {cat === "재원생 후기" && <span className={`${s.badge} ${s.bReview}`}>재원생 후기</span>}
        {cat === "선배 이야기" && <span className={`${s.badge} ${s.bSenior}`}>선배 이야기</span>}
        {cat === "주간 밥상" && <span className={`${s.badge} ${s.bMeal}`}>주간 밥상</span>}
    </span>
);

/** 마우스로 끌어서 가로 스크롤 (터치는 기본 스와이프). 끌었으면 놓을 때 클릭 무시 */
const bindDrag = (el: HTMLElement, onRelease?: () => void, enabled: () => boolean = () => true) => {
    let down = false, sx = 0, sl = 0, moved = false;
    const onDown = (e: PointerEvent) => {
        if (e.pointerType !== "mouse" || !enabled()) return;
        down = true; moved = false; sx = e.clientX; sl = el.scrollLeft;
    };
    const onMove = (e: PointerEvent) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (!moved && Math.abs(dx) > 5) { moved = true; el.classList.add(s.dragging); }
        if (moved) el.scrollLeft = sl - dx;
    };
    const onUp = () => {
        if (!down) return;
        down = false;
        if (!moved) return;
        el.classList.remove(s.dragging);
        onRelease?.();
        const stop = (ev: Event) => { ev.preventDefault(); ev.stopPropagation(); };
        el.addEventListener("click", stop, true);
        setTimeout(() => el.removeEventListener("click", stop, true), 0);
    };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
        el.removeEventListener("pointerdown", onDown);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
    };
};

/* ─────────────── 주간 인기 글 레일 ─────────────── */
const BestRail: React.FC<{ rows: CommunityPost[]; onOpen: (p: CommunityPost) => (e: React.MouseEvent) => void }> = ({ rows, onOpen }) => {
    const ref = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const cntRef = useRef<HTMLSpanElement>(null);

    // 진행 막대·n/8 표시 갱신 + 마우스 드래그(놓으면 카드 단위 스냅)
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => {
            const max = el.scrollWidth - el.clientWidth;
            const cards = el.children.length || 1;
            const vis = Math.max(1, el.clientWidth / (el.scrollWidth / cards));
            const ratio = max > 0 ? el.scrollLeft / max : 0;
            if (barRef.current) {
                const w = Math.min(100, (vis / cards) * 100);
                barRef.current.style.width = w + "%";
                barRef.current.style.left = ratio * (100 - w) + "%";
            }
            if (cntRef.current) {
                const step = el.scrollWidth / cards;
                const idx = ratio >= 0.995 ? cards : Math.min(cards, Math.round(el.scrollLeft / step) + 1);
                cntRef.current.textContent = idx + " / " + cards;
            }
        };
        el.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        const t = setTimeout(update, 50);
        const unbind = bindDrag(el, () => {
            const step = el.scrollWidth / (el.children.length || 1);
            el.scrollTo({ left: Math.round(el.scrollLeft / step) * step, behavior: "smooth" });
        });
        return () => {
            clearTimeout(t);
            el.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
            unbind();
        };
    }, [rows.length]);

    // /stats가 늦게 와서 순서가 바뀌면 scroll-snap(mandatory)이 처음 붙은 카드를 따라가 레일이 중간에서 시작됨 → 처음으로
    const idsKey = rows.map((r) => r.id).join(",");
    useLayoutEffect(() => {
        if (ref.current) ref.current.scrollLeft = 0;
    }, [idsKey]);

    // 좁은 화면은 카드 1장씩, PC는 2장씩
    const nudge = (dir: number) => {
        const el = ref.current;
        if (!el) return;
        const step = el.scrollWidth / (el.children.length || 1);
        const n = window.innerWidth <= 1024 ? 1 : 2;
        el.scrollTo({ left: Math.max(0, (Math.round(el.scrollLeft / step) + dir * n) * step), behavior: "smooth" });
    };

    return (
        <section className={`${s.wrap} ${s.s2}`}>
            <div className={s.s2h}>
                <h2>주간 인기 글</h2>
                <p>* 전 주 월~일 조회수 기준</p>
            </div>
            <div className={s.bestBox}>
                <button type="button" className={`${s.arrow} ${s.arrowL}`} onClick={() => nudge(-1)} aria-label="이전 글"><ChevL /></button>
                <div className={s.hscroll} ref={ref}>
                    {rows.map((b) => (
                        <a key={b.id} className={s.bc} href={"?post=" + b.id} onClick={onOpen(b)}>
                            <span className={`${s.thumb} ${s.bcImg}`}><img src={b.img} alt="" loading="lazy" referrerPolicy="no-referrer" /></span>
                            <Badges cat={b.cat} className={s.bcBadges} />
                            <p className={s.bcT}>{b.title}</p>
                            <p className={s.bcAu}>{b.author}</p>
                        </a>
                    ))}
                </div>
                <button type="button" className={`${s.arrow} ${s.arrowR}`} onClick={() => nudge(1)} aria-label="다음 글"><ChevR /></button>
            </div>
            <div className={s.bestNav}>
                <div className={s.bestTrack}><div ref={barRef} className={s.bestBar} /></div>
                <span ref={cntRef} className={s.bestCnt}>1 / {rows.length || 1}</span>
            </div>
        </section>
    );
};

/* ─────────────── 탭 (모바일: 밑줄 탭 + 좌우 페이드 + 드래그) ─────────────── */
const Tabs: React.FC<{ tab: number; onTab: (n: number) => void }> = ({ tab, onTab }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const scRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = wrapRef.current, sc = scRef.current;
        if (!el || !sc) return;
        const upd = () => {
            el.classList.toggle(s.more, sc.scrollWidth - sc.clientWidth - sc.scrollLeft > 2);
            el.classList.toggle(s.less, sc.scrollLeft > 2);
        };
        sc.addEventListener("scroll", upd, { passive: true });
        window.addEventListener("resize", upd);
        const t = setTimeout(upd, 50);
        const unbind = bindDrag(sc, undefined, () => sc.scrollWidth > sc.clientWidth + 2);
        return () => {
            clearTimeout(t);
            sc.removeEventListener("scroll", upd);
            window.removeEventListener("resize", upd);
            unbind();
        };
    }, []);

    // 선택된 탭이 가려지면 보이는 위치로 스크롤 (scrollIntoView 금지 — 세로 스크롤까지 움직임)
    const pick = (n: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        onTab(n);
        const sc = scRef.current, on = e.currentTarget;
        if (!sc) return;
        const max = sc.scrollWidth - sc.clientWidth;
        let left = sc.scrollLeft;
        if (on.offsetLeft + on.offsetWidth + 24 > left + sc.clientWidth) left = on.offsetLeft + on.offsetWidth + 24 - sc.clientWidth;
        if (on.offsetLeft - 24 < left) left = on.offsetLeft - 24;
        sc.scrollTo({ left: Math.max(0, Math.min(max, left)), behavior: "smooth" });
    };

    return (
        <div className={s.tabsWrap} ref={wrapRef}>
            <div className={s.tabs} ref={scRef} role="tablist">
                {COMMUNITY_TABS.map((t, i) => (
                    <button key={t} type="button" role="tab" aria-selected={tab === i} className={`${s.tab} ${tab === i ? s.tabOn : ""}`} onClick={pick(i)}>{t}</button>
                ))}
            </div>
        </div>
    );
};

/* ─────────────── 본문 ─────────────── */
type ViewBlock =
    | { k: "gap" }
    | { k: "p" | "h" | "quote" | "cap"; text: string }
    | { k: "img"; src: string }
    | { k: "hr" }
    | { k: "link"; text: string; url: string; host: string };

const toBlocks = (bd: PostBody, meta?: CommunityPost): ViewBlock[] => {
    const out: ViewBlock[] = [];
    if ("blocks" in bd) {
        let lastGap = true;
        bd.blocks.forEach((b) => {
            const text = String(b.text || "").replace(/​/g, "").trim();
            // 빈 문단은 연속되면 하나의 간격으로
            if ((b.t === "p" || b.t === "quote" || b.t === "h" || b.t === "cap") && !text) {
                if (!lastGap) out.push({ k: "gap" });
                lastGap = true;
                return;
            }
            lastGap = false;
            if (b.t === "img") out.push({ k: "img", src: b.src || "" });
            else if (b.t === "hr") out.push({ k: "hr" });
            else if (b.t === "link") out.push({ k: "link", text, url: b.url || "", host: String(b.url || "").replace(/^https?:\/\/([^/]+).*/, "$1") });
            else out.push({ k: b.t, text });
        });
    } else if ("preview" in bd && meta) {
        if (meta.img) out.push({ k: "img", src: meta.img });
        out.push({ k: "p", text: meta.summary });
    }
    return out;
};

/** render 'a': 네이버 원본 HTML을 Shadow DOM에 격리 (페이지 CSS와 서로 간섭 없음) */
const ShadowBody: React.FC<{ html: string; css: string; fix: string }> = ({ html, css, fix }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (css && ![].some.call(document.head.querySelectorAll('link[rel="stylesheet"]'), (l: HTMLLinkElement) => l.getAttribute("href") === css)) {
            const l = document.createElement("link");
            l.rel = "stylesheet"; l.href = css;
            document.head.appendChild(l);
        }
        const root = el.shadowRoot || el.attachShadow({ mode: "open" });
        root.innerHTML = (css ? '<link rel="stylesheet" href="' + css + '">' : "") +
            "<style>:host{display:block}" + (fix || "") + "</style>" +
            '<div class="se-viewer se-theme-default" lang="ko-KR"><div class="se-main-container">' + html + "</div></div>";
    }, [html, css, fix]);
    return <div ref={ref} style={{ paddingTop: 40, display: "block" }} />;
};

/* ─────────────── 페이지 ─────────────── */
const Community: React.FC = () => {
    const isMob = useMediaQuery({ query: "(max-width: 640px)" });
    const [params, setParams] = useSearchParams();
    const pid = params.get("post");

    const [raw, setRaw] = useState<RawPost[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [stats, setStats] = useState<Record<string, PostStat>>({});
    const [tab, setTab] = useState(0);
    const [sort, setSort] = useState<"new" | "pop">("new");
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [bodies, setBodies] = useState<Record<string, PostBody>>({});
    const [liked, setLiked] = useState<Record<string, 1>>(loadLiked);
    const [likePend, setLikePend] = useState<LikePend>(loadLikePend);
    const [, force] = useState(0);
    const listY = useRef(0);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const topRef = useRef<HTMLButtonElement>(null);

    const scrollToList = (behavior: ScrollBehavior) => {
        const sec = document.querySelector(`[data-sec="${LIST_LABEL}"]`);
        if (sec) window.scrollTo({ top: sec.getBoundingClientRect().top + (window.scrollY || 0) - 80, behavior });
    };

    useEffect(() => {
        fetchPosts().then((p) => (p ? setRaw(p) : setFailed(true)));
        fetchStats().then((st) => st && setStats(st));
        // 헤더/외부 링크 → ?tab=0~4 으로 해당 탭을 바로 열고 목록으로 이동
        const qt = params.get("tab");
        const tn = qt === null ? NaN : Number(qt);
        if (tn >= 0 && tn <= 4) {
            setTab(tn);
            setTimeout(() => scrollToList("auto"), 60);
        }
        // render 'b': iframe 안 페이지가 보내는 높이로 맞춤 (내부 스크롤 없음)
        const onMsg = (e: MessageEvent) => {
            const d = e.data;
            if (!d || d.type !== "sunbae-post-height") return;
            const cur = new URLSearchParams(window.location.search).get("post");
            if (String(d.id) !== String(cur)) return;
            if (frameRef.current && d.height > 0) frameRef.current.style.height = Math.ceil(d.height) + "px";
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 글이 바뀔 때마다 본문 새로 요청(받기 전엔 이전 응답 유지) + 조회수 1회/세션
    useEffect(() => {
        if (!pid) return;
        window.scrollTo(0, 0);
        setBodies((b) => (b[pid] ? b : { ...b, [pid]: { loading: true } }));
        let alive = true;
        fetchPostBody(pid).then((bd) => alive && setBodies((b) => ({ ...b, [pid]: bd })));
        sendHit(pid);
        return () => { alive = false; };
    }, [pid]);

    // 본문 → 목록 복귀 시 목록 스크롤 위치 복원
    const prevPid = useRef(pid);
    useEffect(() => {
        if (prevPid.current && !pid) {
            const y = listY.current;
            setTimeout(() => window.scrollTo(0, y), 30);
        }
        prevPid.current = pid;
    }, [pid]);

    // 모바일 맨 위로: 한 화면 넘게 내린 상태에서 '위로 스크롤할 때만' 표시
    useEffect(() => {
        let lastY = window.scrollY || 0;
        const upd = () => {
            const el = topRef.current;
            if (!el) return;
            const y = window.scrollY || 0;
            if (y <= window.innerHeight) el.classList.remove(s.topOn);
            else if (y < lastY - 4) el.classList.add(s.topOn);
            else if (y > lastY + 4) el.classList.remove(s.topOn);
            if (Math.abs(y - lastY) > 4) lastY = y;
        };
        window.addEventListener("scroll", upd, { passive: true });
        return () => window.removeEventListener("scroll", upd);
    }, []);

    const openPost = useCallback((id: string) => {
        if (!new URLSearchParams(window.location.search).get("post")) listY.current = window.scrollY || 0;
        setParams({ post: id });
    }, [setParams]);
    const closePost = () => setParams({});
    const onOpen = (p: CommunityPost) => (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey) return; // 새 탭 열기는 그대로
        e.preventDefault();
        openPost(p.id);
    };

    const posts = useMemo(() => (raw ? mergePosts(raw, stats) : []), [raw, stats]);
    const nf = useMemo(newFrom, []);

    // 현재 탭·검색(제목·요약·작성자·카테고리, 공백 무시)·정렬 기준 목록
    const ordered = useMemo(() => {
        const norm = (v: string) => v.replace(/\s+/g, "").toLowerCase();
        const nq = norm(q.trim());
        return posts
            .filter((r) => (tab === 0 || r.cat === COMMUNITY_TABS[tab]) && (!nq || norm(r.title + r.summary + r.author + r.cat).indexOf(nq) !== -1))
            .sort(sort === "pop" ? byKey("views") : byNewest);
    }, [posts, tab, q, sort]);
    const best = useMemo(() => pickBest(posts, 8), [posts]);

    // 썸네일 그리드: PC 4열×2줄=8개, 모바일(≤640) 2열×3줄=6개
    const per = isMob ? 6 : 8;
    const pageCount = Math.max(1, Math.ceil(ordered.length / per));
    const curPage = Math.min(page, pageCount);
    const rows = ordered.slice((curPage - 1) * per, curPage * per);
    const goPage = (n: number) => {
        n = Math.max(1, Math.min(pageCount, n));
        if (n === curPage) return;
        setPage(n);
        scrollToList("smooth");
    };
    // 페이지 번호 5칸 슬라이딩 창: 현재 페이지가 가운데 오도록
    const pgStart = Math.max(1, Math.min(curPage - 2, pageCount - 4));
    const pageNums = [0, 1, 2, 3, 4].map((i) => pgStart + i).filter((n) => n <= pageCount);

    const toggleLike = (id: string, server: number) => {
        const on = !liked[id];
        const nl = { ...liked };
        if (on) nl[id] = 1; else delete nl[id];
        const before = heartsShown(likePend, id, server);
        const np = { ...likePend, [id]: { target: Math.max(0, before + (on ? 1 : -1)), at: Date.now() } };
        setLiked(nl);
        setLikePend(np);
        saveLikes(nl, np);
        sendLike(id, on);
        setTimeout(() => force((x) => x + 1), 91000);
    };

    const hasQ = !!q.trim();

    const renderList = () => (
        <>
            <section className={`${s.wrap} ${s.s1}`}>
                <h1 className={s.h1}>커뮤니티</h1>
            </section>

            {best.length > 0 && <BestRail rows={best} onOpen={onOpen} />}

            <section className={`${s.wrap} ${s.s3}`} data-sec={LIST_LABEL}>
                <div className={s.sortRow}>
                    <label className={s.search}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="5.75" stroke="currentColor" strokeWidth="1.4" /><path d="m13.25 13.25 3.5 3.5" stroke="currentColor" strokeWidth="1.4" /></svg>
                        <input
                            type="search"
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setPage(1); }}
                            onKeyDown={(e) => { if (e.key === "Escape") { setQ(""); setPage(1); } }}
                            placeholder="제목·작성자로 찾기"
                            aria-label="커뮤니티 글 검색"
                        />
                        {hasQ && (
                            <button type="button" className={s.clear} onClick={() => { setQ(""); setPage(1); }} aria-label="검색어 지우기">
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" fill="#CFD3D9" /><path d="m7.25 7.25 5.5 5.5m0-5.5-5.5 5.5" stroke="#FFFFFF" strokeWidth="1.4" /></svg>
                            </button>
                        )}
                    </label>
                    <div className={s.sortBtns}>
                        <button type="button" className={`${s.srt} ${sort === "new" ? s.srtOn : ""}`} onClick={() => { setSort("new"); setPage(1); }}>최신순</button>
                        <span className={s.sortDiv} />
                        <button type="button" className={`${s.srt} ${sort === "pop" ? s.srtOn : ""}`} onClick={() => { setSort("pop"); setPage(1); }}>인기순</button>
                    </div>
                </div>

                <Tabs tab={tab} onTab={(n) => { setTab(n); setPage(1); }} />

                {hasQ && <p className={s.resultLabel}>‘{q.trim()}’ 검색 결과 {ordered.length}건</p>}
                {raw && ordered.length === 0 && (
                    <div className={s.empty}>
                        <p>{hasQ ? "찾는 글이 없어요. 다른 단어로 검색해 보세요." : "아직 글이 없어요."}</p>
                        {hasQ && <button type="button" className={s.ghostBtn} onClick={() => { setQ(""); setPage(1); }}>검색어 지우기</button>}
                    </div>
                )}
                {failed && (
                    <div className={s.empty}>
                        <p>글을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</p>
                    </div>
                )}

                <div className={s.grid}>
                    {rows.map((r) => (
                        <div key={r.id} className={s.card}>
                            <a href={"?post=" + r.id} onClick={onOpen(r)} className={`${s.thumb} ${s.cardImg}`}><img src={r.img} alt="" loading="lazy" referrerPolicy="no-referrer" /></a>
                            <div className={s.cardBody}>
                                <Badges cat={r.cat} isNew={r.date >= nf} />
                                <a href={"?post=" + r.id} onClick={onOpen(r)} className={s.mtitle}>{r.title}</a>
                                <div className={s.meta}>
                                    <span className={s.avatar}><img src={SYMBOL} alt="" /></span>
                                    <span className={s.au}>{r.author}</span>
                                    <span className={`${s.div} ${s.auDiv}`} />
                                    <span>{r.date}</span>
                                    <span className={s.div} />
                                    <span>조회 {fmtN(r.views)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {ordered.length > 0 && (
                    <div className={s.pg}>
                        <button type="button" className={`${s.pgArrow} ${s.pgEnd}`} onClick={() => goPage(1)} aria-label="첫 페이지"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M11.5 4.5 6 10l5.5 5.5M15.5 4.5 10 10l5.5 5.5" stroke="currentColor" strokeWidth="1.3" /></svg></button>
                        <button type="button" className={s.pgArrow} onClick={() => goPage(curPage - 1)} aria-label="이전 페이지"><ChevL w={14} sw={1.3} /></button>
                        {pageNums.map((n) => (
                            <button key={n} type="button" className={`${s.pgNum} ${n === curPage ? s.pgOn : ""}`} aria-current={n === curPage ? "page" : undefined} onClick={() => goPage(n)}>{n}</button>
                        ))}
                        <button type="button" className={s.pgArrow} onClick={() => goPage(curPage + 1)} aria-label="다음 페이지"><ChevR w={14} sw={1.3} /></button>
                        <button type="button" className={`${s.pgArrow} ${s.pgEnd}`} onClick={() => goPage(pageCount)} aria-label="마지막 페이지"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="m4.5 4.5 5.5 5.5-5.5 5.5M8.5 4.5 14 10l-5.5 5.5" stroke="currentColor" strokeWidth="1.3" /></svg></button>
                    </div>
                )}
            </section>
        </>
    );

    const renderPost = (id: string) => {
        const bd: PostBody = bodies[id] || { loading: true };
        const meta = posts.find((p) => p.id === id);
        const title = meta ? meta.title : ("title" in bd && bd.title) || "";
        const hearts = heartsShown(likePend, id, meta ? meta.hearts : 0);
        const idx = ordered.findIndex((p) => p.id === id);
        const prev = idx > 0 ? ordered[idx - 1] : null;
        const next = idx > -1 && idx < ordered.length - 1 ? ordered[idx + 1] : null;
        const blocks = toBlocks(bd, meta);
        const isLiked = !!liked[id];
        return (
            <section className={`${s.wrap} ${s.post}`}>
                <article className={s.article}>
                    <button type="button" className={s.back} onClick={closePost}><ChevL />커뮤니티</button>
                    <div className={s.postBadges}>{meta && <Badges cat={meta.cat} />}</div>
                    <h1 className={s.postH1}>{title}</h1>
                    <div className={s.postMeta}>
                        <span className={s.postAvatar}><img src={SYMBOL} alt="" /></span>
                        <span className={s.au}>{meta ? meta.author : "수능선배"}</span>
                        <span className={s.div} />
                        <span>{meta ? meta.date : ""}</span>
                        <span className={s.div} />
                        <span>조회 {fmtN(meta ? meta.views : 0)}</span>
                    </div>

                    {"loading" in bd && (
                        <div className={s.skel}>
                            <div className={s.skelImg} />
                            <div style={{ width: "92%" }} />
                            <div style={{ width: "84%" }} />
                            <div style={{ width: "60%" }} />
                        </div>
                    )}
                    {"render" in bd && bd.render === "a" && <ShadowBody html={bd.html} css={bd.css} fix={bd.fix} />}
                    {"render" in bd && bd.render === "b" && (
                        <div className={s.bodyFrame}>
                            <iframe ref={frameRef} src={bd.viewUrl} title="글 본문" scrolling="no" />
                        </div>
                    )}
                    {("blocks" in bd || "preview" in bd) && (
                        <div className={s.body}>
                            {blocks.map((k, i) => {
                                switch (k.k) {
                                    case "gap": return <div key={i} className={s.bGap} />;
                                    case "p": return <p key={i}>{k.text}</p>;
                                    case "h": return <h2 key={i}>{k.text}</h2>;
                                    case "quote": return <blockquote key={i}>{k.text}</blockquote>;
                                    case "cap": return <p key={i} className={s.cap}>{k.text}</p>;
                                    case "img": return <span key={i} className={s.postImg}><img src={k.src} alt="" loading="lazy" referrerPolicy="no-referrer" /></span>;
                                    case "hr": return <hr key={i} />;
                                    case "link": return (
                                        <a key={i} className={s.linkCard} href={k.url} target="_blank" rel="noopener noreferrer">
                                            <span>{k.text}</span><span>{k.host}</span>
                                        </a>
                                    );
                                    default: return null;
                                }
                            })}
                        </div>
                    )}
                    {"preview" in bd && <p className={s.previewNote}>본문 연결 전 미리보기예요. 전체 글은 블로그 원문에서 볼 수 있어요.</p>}

                    <div className={s.likeRow}>
                        <button type="button" onClick={() => toggleLike(id, meta ? meta.hearts : 0)} aria-pressed={isLiked} aria-label="좋아요" className={`${s.like} ${isLiked ? s.likeOn : ""}`}>
                            <svg className={s.likeIc} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 16.5s-6.25-3.6-6.25-8.1A3.4 3.4 0 0 1 10 6.3a3.4 3.4 0 0 1 6.25 2.1c0 4.5-6.25 8.1-6.25 8.1Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>
                            <span>좋아요</span>
                            <span>{fmtN(hearts)}</span>
                        </button>
                    </div>

                    <div className={s.listRow}>
                        <button type="button" className={s.solid} onClick={closePost}>목록으로</button>
                    </div>

                    <div className={s.nb}>
                        {prev && (
                            <button type="button" className={s.nbBtn} onClick={() => openPost(prev.id)}>
                                <span>이전 글</span><span className={s.nbT}>{prev.title}</span>
                            </button>
                        )}
                        {next && (
                            <button type="button" className={s.nbBtn} onClick={() => openPost(next.id)}>
                                <span>다음 글</span><span className={s.nbT}>{next.title}</span>
                            </button>
                        )}
                    </div>
                </article>
            </section>
        );
    };

    return (
        <>
            <HeaderTwo />
            <div className={s.page}>
                <div className={s.spacer} />
                {pid ? renderPost(pid) : renderList()}
                <button type="button" ref={topRef} className={s.top} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="맨 위로">
                    <span><svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 15.5V4.5M4.5 10 10 4.5l5.5 5.5" stroke="currentColor" strokeWidth="1.5" /></svg></span>
                </button>
                <div className={s.gap} />
            </div>
            <Footer />
        </>
    );
};

export default Community;
